import React, { useState, useEffect } from 'react';
import './ObservatoryAccessManagement.css';
import { supabase } from '../supabaseClient';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, logOut } from "../firebase";

const ObservatoryAccessManagement = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionStatus, setActionStatus] = useState({}); // Track action status by id
    const [user] = useAuthState(auth);

    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('user_formdata')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setRequests(data || []);
        } catch (error) {
            console.error('Error fetching requests:', error.message);
            alert('Failed to load access requests.');
        } finally {
            setLoading(false);
        }
    };

    const inviteObservatoryAccess = async ({ user_name, email, org_name }) => {
        const baseUrl = process.env.REACT_APP_AUTH_API_BASE_URL;
        if (!baseUrl) {
            throw new Error('Missing REACT_APP_AUTH_API_BASE_URL');
        }

        const response = await fetch(`${baseUrl}/observatory-dashboard/v1/invite-access`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_name, email, org_name, invited_by_email: user.email }),
        });

        if (!response.ok) {
            let message = 'Failed to invite access';
            try {
                const errorData = await response.json();
                message = errorData?.detail || errorData?.message || message;
            } catch {
                // ignore json parse error
            }
            throw new Error(message);
        }

        // Some APIs return empty body on success; don't fail in that case.
        try {
            return await response.json();
        } catch {
            return null;
        }
    };

    const handleAllowAccess = async (request) => {
        const { id, user_name, email } = request;
        
        if (actionStatus[id] === 'loading') return;

        setActionStatus(prev => ({ ...prev, [id]: 'loading' }));

        try {
            if (!email || !email.includes('@')) {
                throw new Error('Invalid email address');
            }

            const [emailPrefix, emailDomain] = email.split('@');
            const finalUserName = (user_name && String(user_name).trim()) ? String(user_name).trim() : emailPrefix;
            const finalOrgName = emailDomain.split('.')[0];

            // console.log('finalUserName', finalUserName);
            // console.log('finalOrgName', finalOrgName);

            await inviteObservatoryAccess({
                user_name: finalUserName,
                email,
                org_name: finalOrgName,
            });

            const { error: accessStatusError } = await supabase
                .from('user_formdata')
                .update({ access_status: true })
                .eq('id', id);

            if (accessStatusError) throw accessStatusError;

            setActionStatus(prev => ({ ...prev, [id]: 'success' }));
            
            // Wait 3 seconds then refresh data (to clear the processed request)
            setTimeout(() => {
                fetchRequests();
                setActionStatus(prev => {
                    const next = { ...prev };
                    delete next[id];
                    return next;
                });
            }, 3000);

        } catch (error) {
            console.error('Error allowing access:', error.message);
            alert('Error: ' + error.message);
            setActionStatus(prev => ({ ...prev, [id]: 'error' }));
        }
    };

    return (
        <div className="observatory-tool-container">
            <div className="observatory-tool-header">
                <div>
                    <h1>Observatory Tool Access Management</h1>
                    <p>Onboard organizations and grant access to the Observatory Tool based on user requests.</p>
                </div>
                <button className="refresh-btn" onClick={fetchRequests} disabled={loading}>
                    <FontAwesomeIcon icon={faSync} spin={loading} /> {loading ? 'Loading...' : 'Refresh Data'}
                </button>
            </div>

            <div className="observatory-table-wrapper">
                {loading ? (
                    <div className="loading-state">Fetching requests...</div>
                ) : (
                    <table className="observatory-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Org Name / Domain</th>
                                <th>Requested At</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.length > 0 ? (
                                requests.map(req => (
                                    <tr key={req.id}>
                                        <td><strong>{req.user_name}</strong></td>
                                        <td>{req.email}</td>
                                        <td>
                                            {req.org_name || (req.email && req.email.includes('@') ? req.email.split('@')[1] : <span className="placeholder-text">Not provided</span>)}
                                        </td>
                                        <td>{new Date(req.created_at).toLocaleString()}</td>
                                        <td className="action-cell">
                                            {req.access_status === true || actionStatus[req.id] === 'success' ? (
                                                <span className="status-success"><FontAwesomeIcon icon={faCheckCircle} /> Access Granted</span>
                                            ) : (
                                                <button 
                                                    className="allow-btn"
                                                    onClick={() => handleAllowAccess(req)}
                                                    disabled={actionStatus[req.id] === 'loading'}
                                                >
                                                    {actionStatus[req.id] === 'loading' ? 'Processing...' : 'Allow Access'}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="empty-state">
                                        No pending access requests found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default ObservatoryAccessManagement;
